<template>
	<el-card :body-style="{ padding: '0' }">
		<div slot="header" class="appointment header">
			<img src="../image/new_facesheet.svg" class="header-icon">
			<span class="head-title-col">Face Sheet</span>
		</div>
		<template v-if="generating">
			<div class="facesheet-loader">
				<span class="icon-wrapper">
					<LoadSpinnerIcon type="primary"></LoadSpinnerIcon>
				</span>
				<h4>
					Loading Face Sheet
				</h4>
				<p v-show="longLoad">
					It might take a while...
				</p>
			</div>
		</template>
		<div v-if="loadedDocument && documentList.length === 0" class="document-container">
			No Face Sheet Available
		</div>
		<el-card class="document" v-for="(document, docIndex) in documentList" :key="document.id">
			<el-row>
				<el-col :span="3">
					<div v-if="document.type === 'orphan' || (document.schedule_ref && document.questionnaire_ref)">
						<span class="section-type">Answered on</span>
						<span class="date-text" v-if="document.created">{{ formatDate(document.created) }}</span>
					</div>
					<div v-else-if="document.type === 'appointment'">
						<span class="cancelled" v-if="document.cancelled">cancelled</span>
						<span class="section-type">Appointment</span>
						<span class="date-text">{{ formatDate(document.schedule_time) }}</span>
						<el-tooltip v-if="document.schedule_ref && !document.questionnaire_ref" class="appointment-progress" placement="bottom" content="Questionnaires Answered" :open-delay="2000">
							<div v-if="document.total && document.meta && document.meta.questionnaire_ids">
								<span class="progress-dot" :style="document.meta.questionnaire_ids.length === document.total ? { background: '#44B358' } : { background: '#FA9E25' }"></span>
								{{ document.meta.questionnaire_ids.length }} of {{ document.total }}
							</div>
							<div v-else-if="document.meta && document.meta.completed && document.meta.total">
								<span class="progress-dot" :style="document.meta.completed === document.meta.total ? { background: '#44B358' } : { background: '#FA9E25' }"></span>
								{{ document.meta.completed }} of {{ document.meta.total }}
							</div>
						</el-tooltip>
					</div>
				</el-col>
				<el-col :span="17" class="emr-content-section" :key="renderKey">
					<span v-if="document.type === 'appointment'" class="display-name">
						<template v-if="document.schedule_ref && !document.questionnaire_ref">
							{{'APPOINTMENT - '}}{{ document.schedule_title }}
						</template>
						<template v-else>
							{{ document.questionnaire_title }}  ({{document.schedule_title}} - {{ formatDate(document.schedule_time) }})
						</template>
					</span>
					<span v-if="document.type === 'orphan'" class="display-name">{{ document.questionnaire_title }}</span>
					<div class="tabs">
						<el-button 
							v-for="docType in Object.keys(docTypeMap)" :key="docType" 
							@click="changeTab(docType, docIndex)" 
							:class="[getDocCount(docType, docIndex) ? '' : 'disabled', tabStateArray[docIndex] === docType ? 'activeTab' : '']"
							>
							{{ getTabName(docType, document) }} ({{ getDocCount(docType, docIndex) }})
						</el-button>
					</div>
					<div v-for="pdf in document.attached_facesheet" :key="pdf.id">
						<template v-if="tabStateArray[docIndex] === pdf.id.split(':')[1]">
							<el-tooltip 
								v-if="pdf.upload_time" 
								placement="bottom" 
								:open-delay="2000">
								<div slot="content">
									Last EMR {{ pdf.upload_type == 'auto' ? 'Sync' : 'Push' }} <br/>
									<b>{{ fomatDateTimeFull(pdf.upload_time, true) }}</b>
								</div>
								<div class="last-emr-push-icon">
									<img src="../image/last_emr_push.png">
								</div>
							</el-tooltip>
							<a class="document-link" :href="getPdfLink(pdf.id, document.provider)" target="_blank">{{ generateFacesheetName(pdf, getDocumentType(document)) }}</a>
							<el-tooltip v-if="document.type === 'appointment' && pdf.created" placement="right" :open-delay="2000">
								<div slot="content">
									Created on: <br/>
									<b>{{ fomatDateTimeFull(pdf.created) }}</b>
								</div>
								<div class="pdf-info-icon">
									<img src="../image/pdf_info.png">
								</div>
							</el-tooltip>
						</template>
					</div>
				</el-col>
				<el-col :span="4">
					<!-- Shows only for integration patients -->
					<div v-if="isImported">
						<button class="send-to-emr-btn" @click="sendToEmr(document)" :disabled="!document.hasOwnProperty('push_emr') || document.push_emr === false">
							Send to EMR 
							<LoadSpinnerIcon type="primary" class="sending-loader" v-if="sendingEmr[document.id]" />
							<img v-else :src="require((!document.hasOwnProperty('push_emr') || document.push_emr === false) ? '../image/send_to_emr_disabled.png' : '../image/send_to_emr.png')" class="send-to-emr-icon">
						</button>
						<p v-if="getPdfUploadInfo(document.attached_facesheet, 'upload_time')" class="emr-status-text">
							Last EMR {{ getPdfUploadInfo(document.attached_facesheet, 'upload_type') == 'auto' ? 'Sync' : 'Push' }} - 
							{{ fomatDateTimeFull(getPdfUploadInfo(document.attached_facesheet, 'upload_time'), true) }}
						</p>
					</div>

					<!-- <el-tooltip v-if="isImported" placement="bottom" :disabled="!document.hasOwnProperty('push_emr') || document.push_emr === false">
						<div slot="content" v-html="getTooltipContent(document)"></div>
					</el-tooltip> -->

				</el-col>
			</el-row>
		</el-card>
	</el-card>
</template>

<script>
import OverviewCard from '..'
import { mapState, mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import format from '@/module/util/format'
import baseUrl from '@/store/action/base-url'
import Loading from '@/module/component/Loading'
import LoadSpinnerIcon from '@/module/component/icon/LoadSpinner'

import SymptomAnalysis from '../mixin/symptom-analysis'
import generate from './generate'
import style from './style'

// import PrintIcon from '../../icon/Print'
import DownloadIcon from '../../icon/Download'
import OpenConsentFormIcon from '../../icon/OpenConsentForm'
import log from 'node-forge/lib/log'


export default {
	name: 'facesheet-card',
	extends: OverviewCard,
	mixins: [ SymptomAnalysis ],

	data() {
		const docTypeMap = {
			'facesheet': 'Face Sheet',
			'consentform': 'Consents',
			'images': 'Photos'
		}
		const subDocTypeMap = {
			'clinical': 'Clinical',
			'drivers_license': 'Driver\'s License',
			'general': 'General',
			'insurance': 'Primary Insurance',
			'secondary_insurance': 'Secondary Insurance',
			'consent': 'Consent',
			'financial_policy': 'Financial Policy',
			'hipaa': 'HIPAA',
			'practice_policy': 'Practice Policy',
			'patient_reported_outcomes': 'Patient Reported Outcomes',
			'registration_demographics': 'Registration / Demographics',
			'surveys': 'Surveys',
			'combined_human_map': 'Body Map'
		}
		return {
			docTypeMap,
			subDocTypeMap,
			generating: false,
			longLoad: false,
			documentList: [],
			tabStateArray: [],
			loadedDocument: false,
			// Dict for storing status for ending emrs
			sendingEmr: {},
			renderKey: 0
		}
	},

	mounted() {
		setTimeout(() => this.longLoad = true, 3000)
		this.loadDocuments(true)
	},

	methods: {
		...format,
		...mapActions(['pdf']),
		...mapActions('provider', [
			'getDocumentHistory',
			'sendDocumentToEmr'
		]),
		getTabName(docType, document) {
			let appointmentTabName = {
				'facesheet': 'Face Sheet',
				'consentform': 'Face Sheet Consents',
				'images': 'Face Sheet Photos'
			}
			let orphanTabName = {
				'facesheet': 'Supporting Data',
				'consentform': 'Consents',
				'images': 'Photos'
			}
			if(document.schedule_ref && !document.questionnaire_ref)
				return appointmentTabName[docType]
			else
				return orphanTabName[docType]

		},
		getDocCount(docType, docIndex) {
			return this.documentList[docIndex].attached_facesheet.filter((pdf) => pdf.id.split(':')[1] === docType).length
		},
		changeTab(tab, docIndex) {
			if (this.getDocCount(tab, docIndex) === 0)
				return
			this.tabStateArray[docIndex] = tab
			this.renderKey += 1
			
		},
		getPdfUploadInfo(facesheets, key) {
			const latestObject = facesheets.reduce((max, current) => {
				return current.upload_time > max.upload_time ? current : max
			}, facesheets[0])
			return latestObject[key]
		},
		getTooltipContent(document) {
			if (!document.hasOwnProperty('push_emr') || document.push_emr === false)
				return 'Pushing Disabled'
			// if (document.type === 'appointment' && document.cancelled)
			// 	return 'Disabled for Cancelled Appointments'
			// else if (document.cancelled)
			// 	return 'Disabled for Cancelled<br>Appointment Questionnaires'
			// else if (!document.autoPush) {
			// 	if(document.type === 'appointment')
			// 		return 'Disabled for Manually Created Events'
			// 	else
			// 		return 'Disabled for Manually Sent Questionnaires'
			// }
		},
		getDocumentType(document) {
			if (document.schedule_ref && !document.questionnaire_ref) return 'appointment'
			else if (document.schedule_ref && document.questionnaire_ref) return 'orphanAppointment'
			else return 'orphan'
		},
		getPdfLink(documentId, provider) {
			const providerId = provider.split(':')[0]
			return `${baseUrl}/provider/${providerId}/document/${documentId}`
		},

		sendToEmr(document) {
			Vue.set(this.sendingEmr, document.id, true)
			this.sendingEmr[document.id] = true
			this.sendDocumentToEmr({
				provider: document.provider.split(':')[0],
				patient: this.patient.id,
				documentIdObj: {
					document_id: document.attached_facesheet.map(d => d.id)
				}
			})
			.then((_) => {
				this.loadDocuments(false).then((_) => {
					Vue.set(this.sendingEmr, document.id, false)
				})
			})
			.catch((err) => {
				this.$message.error('Error sending to EMR')
				
			}).finally((_) => {
				Vue.set(this.sendingEmr, document.id, false)
			})
		},
		loadDocuments(showLoader = true) {
			let providers = [... new Set(this.patientProviderProfileList.map(p => p.provider))]
			if(showLoader)
				this.generating = true
			return Promise.all(providers.map(provider => {
				 return this.getDocumentHistory({ 
					provider, 
					patient: this.patient.id 
				}).then((res) => {
					if(!res) throw new Error('No response from server')
					return res.documents
				}).catch((err) => {
					console.log(err)
					return []
				})
			})).then((res) => {
				this.documentList = this.bucketDocumentList(res.flatMap(r => r))
				// Check for `schedule_title` in documentList and 
				// load default tab state in tabStateArray
				this.tabStateArray = this.documentList.map(_ => 'facesheet')
				this.loadedDocument = true
			}).catch((err) => {
				console.log(err)
			}).finally((_) => {
				this.generating = false
			})
		},
		bucketDocumentList(documents) {
			let documentList = []
			
			documents.reduce((acc, docListObj) => {
				const {
					id, created, schedule_title, upload_time, upload_type, questionnaire_title, schedule_ref, document_name
				} = docListObj
				let { questionnaire_ref } = docListObj
				let autoPush = false
				let schedule = schedule_ref ? (this.patientScheduleList.find((schedule) => schedule.id === schedule_ref)) : undefined
				let cancelled = schedule ? false : true
				if (schedule_ref && schedule) {
					if(schedule.athena || schedule.ecw || schedule.drchrono || schedule.carecloud)
						autoPush = true
				}
					
				// For appointment facesheet
				if(schedule_ref && !questionnaire_ref) {
					// For appointment facesheet before T0
					if(!acc[schedule_ref]) {
						acc[schedule_ref] = {
							...docListObj,
							cancelled,
							autoPush,
							type: 'appointment',
							attached_facesheet: []
						}
						// Find the schedule object from patientScheduleList by searching for schedule_ref
						// If schedule object is not found then it means the appointment is cancelled
						const scheduleObj = this.patientScheduleList.find((schedule) => schedule.id === schedule_ref)
						if(!schedule_title && scheduleObj) {
							try {
								acc[schedule_ref].schedule_title = scheduleObj.title
								acc[schedule_ref].schedule_time = scheduleObj.time
							} catch (error) {
								console.error('Error in loading bucket list', error)
							}
							
						}
						if(scheduleObj && docListObj && docListObj.meta && docListObj.meta.questionnaire_ids) {
							try {
								const totalQuestionnaires = scheduleObj.scheduleContent.message
									.filter(({ delay }) => delay <= 0)
									.flatMap(({ attachment }) => attachment.filter(({ ref_type }) => ref_type === 'questionnaire'))
								acc[schedule_ref].total = totalQuestionnaires.length
							} catch (error) {
								console.error('Error in loading bucket list', scheduleObj, Array.isArray(scheduleObj.scheduleContent.message))
							}
						}
					}

					//Check if attached facesheet has of same type already than update if latest one (check `created` key)
					const docType = id.split(':')[1]
					const hasSubDocType = id.split(':').length === 4
					// Check for index of attached facesheet which matches the docType
					const attachedFacesheetIndex = acc[schedule_ref].attached_facesheet
													.findIndex((facesheet) => facesheet.id.split(':')[1] === docType && (hasSubDocType ? (facesheet.id.split(':')[2] === id.split(':')[2]) : true))
					if(attachedFacesheetIndex > -1 ) {
						// If matched facehseet is newer than current one then update
						if(acc[schedule_ref].attached_facesheet[attachedFacesheetIndex].created < created && (docListObj.meta && docListObj.meta.total)) {
							// Overwrite Meta
							if('meta' in docListObj)
								acc[schedule_ref].meta = docListObj.meta
							// Overwrite attached facesheet
							acc[schedule_ref].attached_facesheet[attachedFacesheetIndex] = {
								id,
								created,
								schedule_title,
								upload_time,
								upload_type,
								document_name

							}
						}
						else {
							// Add questionnaire ids from meta and uniqify
							if('meta' in docListObj && 'questionnaire_ids' in docListObj.meta)
								acc[schedule_ref].meta.questionnaire_ids.push(...docListObj.meta.questionnaire_ids)
							acc[schedule_ref].attached_facesheet.push({
								id,
								created,
								schedule_title,
								upload_time,
								upload_type,
								document_name
							})
						}
					} else {
						if('meta' in docListObj && 'questionnaire_ids' in docListObj.meta)
							acc[schedule_ref].meta.questionnaire_ids.push(...docListObj.meta.questionnaire_ids)
						acc[schedule_ref].attached_facesheet.push({
							id,
							created,
							schedule_title,
							upload_time,
							upload_type,
							document_name
						})
					}
				} else if (schedule_ref && questionnaire_ref) {
					// For appointment facesheet for T+X
					if(!acc[schedule_ref + '-' + questionnaire_ref])
						acc[schedule_ref + '-' + questionnaire_ref] = {
							...docListObj,
							cancelled,
							autoPush,
							type: 'appointment',
							attached_facesheet: []
						}
					
					// Attaching facesheet
					acc[schedule_ref + '-' + questionnaire_ref].attached_facesheet.push({
						id,
						created,
						questionnaire_title,
						upload_time,
						upload_type,
						document_name
					})
				} else {
					// if questionnaire_ref is not present then it is a new api response
					if(!questionnaire_ref || !docListObj.questionnaire_title) {
						questionnaire_ref = docListObj.meta && docListObj.meta.questionnaire_ids[0]
						docListObj.questionnaire_title = this.getQuestionnaireTitleFromRef(questionnaire_ref)
					}
					if(!acc[questionnaire_ref])
						acc[questionnaire_ref] = {
							...docListObj,
							type: 'orphan',
							attached_facesheet: []
						}
					const { id, created, questionnaire_title } = docListObj
					acc[questionnaire_ref].attached_facesheet.push({
						id,
						created,
						questionnaire_title,
						upload_time,
						upload_type,
						document_name
					})
				}
				documentList = acc
				return acc
			}, {})

			Object.values(documentList).forEach(doc => {
				if(doc.meta && doc.meta.questionnaire_ids)
					doc.meta.questionnaire_ids = [...new Set(doc.meta.questionnaire_ids)]

				doc.attached_facesheet.sort((docA, docB) => docB.created - docA.created)
				
				// find the element in doc.attached_facesheet which has 'combined_human_map' in it's id,
				// then move that element to the last position of the array
				const combinedHumanMapIndex = doc.attached_facesheet.findIndex(fs => fs.id.split(':')[2] === 'combined_human_map')
				if(combinedHumanMapIndex > -1) {
					const combinedHumanMap = doc.attached_facesheet.splice(combinedHumanMapIndex, 1)
					doc.attached_facesheet.push(combinedHumanMap[0])
				}
			})
			return Object.values(documentList).sort((docA, docB) => {
				let docATime = docA.created, docBTime = docB.created
				if(docA.type === 'appointment' && !docA.questionnaire_ref)
					docATime = docA.schedule_time || 0
				if(docB.type === 'appointment' && !docB.questionnaire_ref)
					docBTime = docB.schedule_time || 0
				return docBTime - docATime
			})
		},
		sortPdfs(pdfs) {
			if (pdfs.length > 1) {
				return pdfs.sort((a, b) => {
					const aType = a.id.split(':')[1]
					const bType = b.id.split(':')[1]
					const typeOrder = ['facesheet', 'consentform', 'images']
					return typeOrder.indexOf(aType) - typeOrder.indexOf(bType)
				})
			}
			return pdfs
		},
		printFacesheet() {
			// document.getElementById('facesheetIframe').contentWindow.print();
			let objFrame = document.getElementById('facesheetIframe')
			objFrame.contentWindow.focus()
			objFrame.contentWindow.print()
			// window.frames["facesheetIframe"].focus();
			// window.frames["facesheetIframe"].print();
		},
		downloadFacesheet() {
			window.open(this.src + '&download=true')
		},
		openConsentForm() {
			window.open(this.consentLink, '_blank')
		},
		openPhoto() {
			window.open(this.photoLink, '_blank')
		},
		catgSelected(data) {
			if(data === 'other') window.open(this.photoLink, '_blank')
			else window.open(this.photoLink + '&category=' + data, '_blank')
		},
		/**
		 * Generate name for a facesheet by taking in document object
		 */
		generateFacesheetName(document, eventOrOrphan) {
			if(document.document_name)
				return document.document_name
			// Adds document type to facesheet name
			let facesheetName = this.docTypeMap[document.id.split(':')[1]]

			// For T+N facesheet names should start with Face Sheet, Face Sheet Consents, Face Sheet Photos
			if (eventOrOrphan === 'appointment') {
				if (!facesheetName.includes('Face Sheet')) 
					facesheetName = 'Face Sheet ' + facesheetName
			} else { // For T=0 & T-N facesheet names should start with '', Consents, Photos
				facesheetName = facesheetName.replace('Face Sheet', '')
			}

			// Checks if sub document type exists in id
			// document.id eg: { id: "document:facesheet:general:01HGWSTX6ZT6R5T6ZZ6XCV7ZNM" }
			if (document.id.split(':').length === 4) {
				let subDocType = document.id.split(':')[2]
				if (subDocType !== 'default')
					facesheetName += (facesheetName.length ? ' - ' : '') + this.subDocTypeMap[subDocType]
			}

			if(eventOrOrphan === 'appointment')
				facesheetName += ' - ' + document.schedule_title
			else
				facesheetName += (facesheetName.length ? ' - ' : '') + document.questionnaire_title
			facesheetName += ' - ' + this.formatDate(document.created) + (document.id.includes('images') ? '.jpeg' : '.pdf')
			return facesheetName
		},
		getQuestionnaireTitleFromRef(questionnaireRef) {
			try {
				const questionEvent = this.patientEvents.find(({ id }) => id === questionnaireRef)
				const questionnaireId = questionEvent.type.split(':')[1]
				return this.getContentKey('questionnaire', questionnaireId, 'title')
			} catch (error) {
				return 'Questionnaire'
			}
			
		}
	},

	computed: {
		...mapState(['baseUrl']),
		...mapGetters('content', [
			'getContent',
			'getContentKey'
		]),
		...mapGetters('provider', [
			'getCurrentProvider',
			'getPatientEventAnalysis',
			'getPatientProviderProfileList',
			'getPatientSchedule',
			'getPatientMessage'
		]),
		patientScheduleList() {
			return this.getPatientSchedule(this.patient.id).map((schedule) => {
				return {
					...schedule,
					title: this.getContentKey('event', schedule.event, 'name'),
					scheduleContent: this.getContent('event', schedule.event)
				}
			})
		},
		imageQuestCategExist() {
			let catValues = this.patientEventType('questionnaire')
				.filter((quest) => quest.completed)
				.flatMap(({type, question}) => {
					let imageQuestions = this.getContent('questionnaire', type.split(":")[1])
						.question
						.filter((questContent) => questContent.type === 'image_upload' && questContent.category_type !== undefined)
						.filter((questContent) => question.some(({ id, answer }) => id === questContent.id && answer.some(({ value }) => value.includes('cdn'))))
					return imageQuestions
				})
				.map((img_quest) => img_quest.category_type)

			return catValues.length > 0 ? [...new Set(catValues), 'other'] : [] //// remove duplicate strings and add 'other' if category exist
		},
		patientProviderProfileList() {
			return this.getPatientProviderProfileList(this.patient.id)
		},
		patientAnalysis() {
			return this.getPatientEventAnalysis(this.patient.id)
		},
		patientCondition() {
			var profile = this.getContent('profile', this.patient.profile[0])
			return (profile) ? profile.title : ''
		},
		src() {
			let provider = this.getCurrentProvider
			if(!provider)
				provider = this.patientProviderProfileList.map(({provider}) => provider).join(',')
			return this.baseUrl + '/document/facesheet/' + this.patient.id + '?provider=' + provider + '&auth_token=' + this.getJWT
		},
		consentLink() {
			let provider = this.getCurrentProvider
			if(!provider)
				provider = this.patientProviderProfileList.map(({provider}) => provider).join(',')
			return this.baseUrl + '/document/consentform/' + this.patient.id + '?provider=' + provider + '&auth_token=' + this.getJWT
		},
		photoLink() {
			let provider = this.getCurrentProvider
			if(!provider)
				provider = this.patientProviderProfileList.map(({provider}) => provider).join(',')
			return this.baseUrl + '/document/images/' + this.patient.id + '?provider=' + provider + '&auth_token=' + this.getJWT
		},
		getJWT() {
			return localStorage.getItem('jwtToken')
		},
		isImported() {
			if(this.patient.athena || this.patient.ecw || this.patient.drchrono || this.patient.carecloud)
				return true
			return false
		}
	},

	components: {
		LoadSpinnerIcon,
		// PrintIcon,
		OpenConsentFormIcon
	}
}
</script>

<style lang="scss" scoped>
@import "../style.scss";
@import "@/module/app/theme/color.scss";
iframe {
	width: 100%;
	height: 800px;
	border: none;
	position: absolute;
	top: 40px;
}
.document {
	border-radius: 4px;
	width: 99%;
	margin-left: 5px;
	color: #333333;

	.cancelled {
		width: 64px;
		height: 13px;
		background-color: #DA4E4E;
		text-transform: uppercase;
		color: #ffffff;
		padding: 2px 4px;
		font-size: 10px;
		font-weight: 600;
		display: block;
		margin-bottom: 9px;
	}
	
	.section-type {
		display: block;
		font-size: 10px;
		font-weight: 400;
		line-height: 10px;
		letter-spacing: 0px;
		text-align: left;
	}
	.date-text {
		padding: 8px 0;
		display: block;
		font-size: 12px;
		font-weight: 600;
		line-height: 12px;
		letter-spacing: 0px;
		text-align: left;
	}
	.appointment-progress {
		font-size: 10px;
		font-weight: 500;
		line-height: 12px;
		letter-spacing: 0px;
		text-align: left;
		width: 50px;
		cursor: pointer;
		.progress-dot {
			display: inline-block;
			height: 8px;
			width: 8px;
			border-radius: 50%;
		}
	}
	.sending-loader {
		width: 12px;
		height: 12px;
		float: right;
	}

	.tabs {
		margin-top: 10px;
		margin-bottom: 10px;

		border-bottom: 1px solid #EEEEEE;


		.el-button {
			border: 1px solid #EEEEEE;
			padding: 5px 10px;
			font-size: 12px;
			font-weight: 500;
			line-height: 12px;
			letter-spacing: 0em;
			color: #525252;

			&.activeTab {
				background-color: #EEEEEE;
				color: #333333;
				font-weight: 700;
			}
			&.disabled {
				background-color: #FFFFFF;
				color: #999999;
				cursor: not-allowed;
			}
		}
		.el-button:first-child {
			border-top-left-radius: 4px;
		}

		.el-button:not(:first-child) {
			margin-left: 0;
		}

		.el-button:last-child {
			border-top-right-radius: 4px;
		}
	}
}
.document-container {
	margin: 40px 10px;
}
.document:first-child {
	margin-top: 20px;
}
.emr-status-text {
	display: inline-block;
	font-size: 12px;
	font-weight: 400;
	line-height: 15px;
	letter-spacing: 0px;
	text-align: right;
	color: #666666;
	float: right;
	margin-top: 7px;

}
.emr-content-section {

	.display-name {
		font-size: 14px;
		font-weight: 700;
		line-height: 14px;
		letter-spacing: 0px;
		text-align: left;
		display: inline-block;
		margin-bottom: 5px;
	}

}
.send-to-emr-btn {
	width: 109px;
	height: 24px;
	border-radius: 4px;
	border: none;
	font-size: 12px;
	font-weight: 500;
	line-height: 12px;
	letter-spacing: 0em;
	float: right;
}
.send-to-emr-btn[disabled] {
	cursor: no-drop;
	background-color: #EEEEEE;
	color: #B2B2B2;
}
.send-to-emr-icon {
	width: 12px;
	height: 12px;
	float: right;
}
.document-link {
	line-height: 25px;
	color: #3754FF;
	font-size: 14px;
	text-decoration: none;
	border-bottom: 1px solid;
}
.last-emr-push-icon {
	display: inline-block;
	cursor: pointer;
	img {
		width: 14px;
		height: 14px;
		margin-left: -20px;
		margin-bottom: -3px;
	}
}
.pdf-info-icon {
	display: inline-block;
	cursor: pointer;
	img {
		width: 14px;
		height: 14px;
		margin-left: 5px;
		margin-bottom: -3px;
	}
}
.header {
	.right-aligned {
		margin-left: auto;
	}

	.ml-20 {
		margin-left: 20px;
	}
	.el-button.header-action {
		padding: 0px 6px;
		line-height: 1;
		// height: 27px;
		font-size: 14px;
		font-weight: 600;
		background-color: #3473b9;
		border: none;
		position: initial;
	}
	.el-button.open-consent-form {
		background-color: #E28816;
	}
}
.facesheet-loader {
	margin-top: 150px;
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 50px;
	.icon-wrapper {
		width: 25px;
		height: 25px;
		display: block;
	}
	h4 {
		margin-top: 15px;
		font-size: 18px;
		color: $primary-color;
	}
	p {
		font-size: 16px;
		font-weight: 500;
		line-height: 19px;
		color: $grey-500;
		margin-top: 8px;
	}
}
.cust-dropdwn {
	margin-right: 0px !important;

	button {
		.arr-icon-align{
			position: relative;
			left: 8px;
		}
	}
}
.dropdwn-item-cont {
	min-width: 100px;
	.el-dropdown-menu__item.dropdwn-item {
		text-transform: capitalize;
	}
}
</style>
<style lang="scss">
.el-button.header-action {
	span {
		display: flex;
		padding: 7px 10px;
	}
}
</style>
<style scoped>
.header-icon {
	height: 43px !important;
	margin-top: 0px !important;
}
</style>
